import { createApi } from "@reduxjs/toolkit/query/react";
import { prepareBaseQuery } from "./baseQueryWithReauth";
import eventApi from "./event";
import { adminApi } from "./admin";

// After a request write succeeds, refetch the admin v2 Portal inbox pages
// and the sidebar open count (adminApi, another cache, tag AdminPortalInbox),
// whichever screen the write came from. Exported for portalReplyApi.js.
export async function invalidatePortalInbox(dispatch, queryFulfilled) {
  try {
    await queryFulfilled;
    dispatch(adminApi.util.invalidateTags(["AdminPortalInbox"]));
  } catch {
    // The calling component reports the error.
  }
}

// Admin side of the client portal (djr-be routes/events.js, admin JWT):
//   GET   /events/:eventId/portal-access          state and the live link
//   POST  /events/:eventId/portal-access/enable   idempotent, never rotates
//   POST  /events/:eventId/portal-access/rotate   destructive
//   POST  /events/:eventId/portal-access/revoke   disable, keep the version
//   POST  /events/:eventId/portal-access/send     send or resend the invite
//   PATCH /events/:eventId/portal-access/money    { showMoney }
//   GET   /events/:eventId/portal-requests        the client's requests
//   PATCH /events/:eventId/portal-requests/:id    { status?, adminReply? }
//   POST  /events/:eventId/portal/parse           { text, target }, writes nothing
//   PATCH /events/:eventId/portal/client-data     { clientPortal, ifUpdatedAt }
//   GET   /events/:eventId/run-sheet              Andrew's own rows, never client-visible
//   PATCH /events/:eventId/run-sheet              { runSheet: { rows }, ifUpdatedAt? }
//   PATCH /events/:eventId/setup-minutes          { setupMinutesBefore }
//
// These need the admin Bearer token, so they use baseQueryWithReauth like
// src/services/songRequests.js. They must never go in src/services/portal.js,
// which is the anonymous client API.
//
// The status payload carries the portal token and URL. Nothing here logs it.

// Enable, rotate and money answer with the full status payload. Write it
// straight into the cached status so the panel updates without a refetch.
async function mergeIntoStatus(eventId, dispatch, queryFulfilled, pick) {
  try {
    const { data } = await queryFulfilled;
    if (!eventId || !data) return;
    const patch = typeof pick === "function" ? pick(data) : data;
    dispatch(
      portalAdminApi.util.updateQueryData(
        "getPortalAccessStatus",
        eventId,
        (draft) => ({ ...(draft ?? {}), ...(patch ?? {}) }),
      ),
    );
  } catch {
    // The calling component reports the error.
  }
}

const portalAdminApi = createApi({
  reducerPath: "portalAdmin",
  baseQuery: prepareBaseQuery(""),
  tagTypes: ["PortalAccess", "PortalRequests", "RunSheet"],
  endpoints: (builder) => ({
    getPortalAccessStatus: builder.query({
      query: (eventId) => `/events/${encodeURIComponent(eventId ?? "")}/portal-access`,
      providesTags: (result, error, eventId) => [
        { type: "PortalAccess", id: eventId },
      ],
    }),

    // Idempotent on the server: while the link is live it writes nothing and
    // returns the same link. Never rotates.
    enablePortalAccess: builder.mutation({
      query: (eventId) => ({
        url: `/events/${encodeURIComponent(eventId ?? "")}/portal-access/enable`,
        method: "POST",
        body: {},
      }),
      async onQueryStarted(eventId, { dispatch, queryFulfilled }) {
        await mergeIntoStatus(eventId, dispatch, queryFulfilled);
      },
    }),

    // Destructive: tokenVersion += 1, so the link already emailed to the
    // client stops working at once.
    rotatePortalAccess: builder.mutation({
      query: (eventId) => ({
        url: `/events/${encodeURIComponent(eventId ?? "")}/portal-access/rotate`,
        method: "POST",
        body: {},
      }),
      async onQueryStarted(eventId, { dispatch, queryFulfilled }) {
        await mergeIntoStatus(eventId, dispatch, queryFulfilled);
      },
    }),

    // Revoke answers with a partial payload, so refetch the full status.
    revokePortalAccess: builder.mutation({
      query: (eventId) => ({
        url: `/events/${encodeURIComponent(eventId ?? "")}/portal-access/revoke`,
        method: "POST",
        body: {},
      }),
      invalidatesTags: (r, e, eventId) =>
        eventId ? [{ type: "PortalAccess", id: eventId }] : [],
    }),

    // Sends the current link. Never rotates. Answers { sentAt, sentCount }.
    sendPortalInvite: builder.mutation({
      query: (eventId) => ({
        url: `/events/${encodeURIComponent(eventId ?? "")}/portal-access/send`,
        method: "POST",
        body: {},
      }),
      async onQueryStarted(eventId, { dispatch, queryFulfilled }) {
        await mergeIntoStatus(eventId, dispatch, queryFulfilled, (data) => ({
          ...(data?.sentAt ? { sentAt: data.sentAt } : {}),
          ...(Number.isInteger(data?.sentCount)
            ? { sentCount: data.sentCount }
            : {}),
        }));
      },
    }),

    setPortalShowMoney: builder.mutation({
      query: ({ eventId, showMoney }) => ({
        url: `/events/${encodeURIComponent(eventId ?? "")}/portal-access/money`,
        method: "PATCH",
        body: { showMoney: showMoney === true },
      }),
      async onQueryStarted({ eventId }, { dispatch, queryFulfilled }) {
        await mergeIntoStatus(eventId, dispatch, queryFulfilled);
      },
    }),

    // Newest first. { eventId, requests, total, newCount }
    listPortalRequests: builder.query({
      query: (eventId) => `/events/${encodeURIComponent(eventId ?? "")}/portal-requests`,
      providesTags: (result, error, eventId) => [
        { type: "PortalRequests", id: eventId },
      ],
    }),

    // Writes the request row only, never the event. A non-empty adminReply
    // stamps repliedAt on the server; an empty one clears it.
    updatePortalRequest: builder.mutation({
      query: ({ eventId, requestId, status, adminReply }) => ({
        url: `/events/${encodeURIComponent(eventId ?? "")}/portal-requests/${encodeURIComponent(requestId ?? "")}`,
        method: "PATCH",
        body: {
          ...(status !== undefined ? { status } : {}),
          ...(adminReply !== undefined ? { adminReply } : {}),
        },
      }),
      invalidatesTags: (r, e, { eventId }) =>
        eventId ? [{ type: "PortalRequests", id: eventId }] : [],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        await invalidatePortalInbox(dispatch, queryFulfilled);
      },
    }),

    // The capture box on Andrew's side (task 2026-09-17_0910, Part 6.2).
    // The parse writes nothing, so it invalidates nothing. Answers
    // { ops, unmatched, source, sourcePreview, removed, suspectNumbers,
    //   droppedForMoney, clientAsks, clientPortalUpdatedAt }.
    //
    // `target` (task 2026-09-17_1715, Part 4.1) is "clientPortal" (the
    // default on the server, current behaviour) or "runSheet", where the
    // money filter is skipped and `removed` comes back null.
    parseAdminCapture: builder.mutation({
      query: ({ eventId, text, target }) => ({
        url: `/events/${encodeURIComponent(eventId ?? "")}/portal/parse`,
        method: "POST",
        body: { text, target },
      }),
    }),

    // Applies confirmed rows to clientPortal, stamped updatedBy: admin.
    // ifUpdatedAt is always sent, null included (decision 6): null means
    // "I expect no client save yet"; only an absent key is unconditional.
    // A 409 PORTAL_STALE means the client saved in between.
    //
    // The Day rail reads GET /events/by-id from the event api, a different
    // createApi, so a tag here would not reach it: invalidate that one
    // event by hand (getEventById provides { type: "Event", id }). The list
    // query's plain "Event" tag is not touched. A 409 PORTAL_STALE also
    // refetches, so the rerun starts from the client's newer copy.
    applyAdminPortalUpdate: builder.mutation({
      query: ({ eventId, clientPortal, ifUpdatedAt }) => ({
        url: `/events/${encodeURIComponent(eventId ?? "")}/portal/client-data`,
        method: "PATCH",
        body: { clientPortal: clientPortal ?? {}, ifUpdatedAt: ifUpdatedAt ?? null },
      }),
      async onQueryStarted({ eventId }, { dispatch, queryFulfilled }) {
        const refetch = () => {
          if (eventId) dispatch(eventApi.util.invalidateTags([{ type: "Event", id: eventId }]));
        };
        try {
          await queryFulfilled;
          refetch();
        } catch (err) {
          // The calling component reports the error.
          if (err?.error?.status === 409) refetch();
        }
      },
    }),

    // Andrew's own run sheet (task 2026-09-17_1715, Part 7.2). Never
    // client-visible; the server answers { runSheet: { rows, updatedAt } }
    // on GET and PATCH alike, with rows [] and updatedAt null when absent.
    getRunSheet: builder.query({
      query: (eventId) => ({ url: `/events/${encodeURIComponent(eventId ?? "")}/run-sheet` }),
      providesTags: (r, e, eventId) => [{ type: "RunSheet", id: eventId }],
    }),

    // The whole array, every time; a done tick is a save like any other.
    // ifUpdatedAt is sent whenever it is not undefined: null is a real value
    // ("I expect nothing saved yet", decision 3.3) and a truthy guard would
    // drop it. A 409 RUN_SHEET_STALE means another save landed first.
    //
    // The PATCH answer carries the stored rows and the new stamp, so it is
    // written into the cached GET and that is the whole refresh: the next
    // tick's ifUpdatedAt is the one this save just wrote (decision 6), and
    // no tag refetch follows (it would only re-read what was just written).
    // A 409 does refetch, so the sheet on screen is the saved one.
    updateRunSheet: builder.mutation({
      query: ({ eventId, rows, ifUpdatedAt }) => ({
        url: `/events/${encodeURIComponent(eventId ?? "")}/run-sheet`,
        method: "PATCH",
        body: { runSheet: { rows: rows ?? [] }, ...(ifUpdatedAt !== undefined ? { ifUpdatedAt } : {}) },
      }),
      async onQueryStarted({ eventId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (eventId && data?.runSheet) {
            dispatch(
              portalAdminApi.util.updateQueryData("getRunSheet", eventId, (draft) => ({
                ...(draft ?? {}),
                runSheet: data.runSheet,
              })),
            );
          }
        } catch (err) {
          // The calling component reports the error.
          if (err?.error?.status === 409 && eventId) {
            dispatch(portalAdminApi.util.invalidateTags([{ type: "RunSheet", id: eventId }]));
          }
        }
      },
    }),

    // The one writer setupMinutesBefore has. Answers { msg, setupMinutesBefore }.
    updateSetupMinutes: builder.mutation({
      query: ({ eventId, setupMinutesBefore }) => ({
        url: `/events/${encodeURIComponent(eventId ?? "")}/setup-minutes`,
        method: "PATCH",
        body: { setupMinutesBefore },
      }),
      // The arrival stop is computed from the event, which lives in the OTHER
      // api. Mirror applyAdminPortalUpdate: the id-scoped tag only. Its own
      // comment says the list query's plain "Event" tag is deliberately not
      // touched.
      async onQueryStarted({ eventId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          if (eventId) dispatch(eventApi.util.invalidateTags([{ type: "Event", id: eventId }]));
        } catch {
          // the calling component reports the error
        }
      },
    }),
  }),
});

export const {
  useGetPortalAccessStatusQuery,
  useEnablePortalAccessMutation,
  useRotatePortalAccessMutation,
  useRevokePortalAccessMutation,
  useSendPortalInviteMutation,
  useSetPortalShowMoneyMutation,
  useListPortalRequestsQuery,
  useUpdatePortalRequestMutation,
  useParseAdminCaptureMutation,
  useApplyAdminPortalUpdateMutation,
  useGetRunSheetQuery,
  useUpdateRunSheetMutation,
  useUpdateSetupMinutesMutation,
} = portalAdminApi;

export default portalAdminApi;
export { portalAdminApi };
